import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import {
  useCustomerTasks,
  useRemoveCustomerTask,
  useSetCustomerTaskStatus,
} from "../hooks";
import { useUsers } from "../../users/hooks";
import { useMe } from "../../users/hooks/useMe";
import { CustomerSelectSearch } from "../../customers/components";
import { cleanObject } from "../../../helpers/clean-object";
import { CustomerTaskStatus, UserRole } from "../../../__generated__/globalTypes";
import { customerTaskStatusLabel, customerTaskPriorityInfo } from "../../../helpers/enum-labels";
import { formatDisplayDate } from "../../../helpers/date";
import { dueDateStatus, DUE_DATE_STYLES, DUE_DATE_LABELS } from "../due-date";

interface ISearchForm {
  customerId?: string;
  assignedToId?: string;
  status?: string;
  scope?: string;
}

export const CustomerTasks: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: usersData } = useUsers({ where: {}, limit: 100, offset: 0 });
  const { data: meData } = useMe();
  const [remove] = useRemoveCustomerTask();
  const { setStatus } = useSetCustomerTaskStatus();

  const isAdmin = meData?.me?.role === UserRole.Admin;

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      customerId: searchParams.get("customerId") ?? "",
      assignedToId: searchParams.get("assignedToId") ?? "",
      status: searchParams.get("status") ?? "",
      scope: searchParams.get("scope") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/customer-tasks",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const customerId = searchParams.get("customerId");
  const assignedToId = searchParams.get("assignedToId");
  const status = searchParams.get("status");
  const scope = searchParams.get("scope");

  const where: any = {};
  if (customerId) where.customerId = +customerId;
  if (assignedToId) where.assignedToId = +assignedToId;
  if (status) where.status = status;

  const { data, loading, refetch } = useCustomerTasks({ where });

  // `customerTasks(where)` ne sait pas filtrer sur l'absence de client : on le fait ici.
  const results = (data?.customerTasks?.results ?? []).filter((task) => {
    if (scope === "internal") return !task.customerId;
    if (scope === "customer") return !!task.customerId;
    return true;
  });

  const handleRemove = async (id: number) => {
    if (!window.confirm("Supprimer cette tâche ? Cette action est irréversible.")) return;
    try {
      const { data } = await remove({ variables: { id } });
      if (data?.removeCustomerTask?.ok) {
        toast.success("La tâche a été supprimée");
        refetch();
      } else {
        toast.error(data?.removeCustomerTask?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>, id: number) => {
    e.stopPropagation();
    setStatus(id, e.target.value as CustomerTaskStatus);
  };

  return (
    <>
      <Header
        title="Tâches"
        subtitle="Suivi des tâches par client"
        buttons={[
          {
            actionText: "Nouvelle tâche",
            bgColor: "indigo",
            textColor: "white",
            link: `/customer-task/create`,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder=""
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Assigné à" className="w-full" {...form.register("assignedToId")}>
                <option value="">Tous</option>
                {usersData?.users?.results?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname}
                  </option>
                ))}
              </Select>
              <Select label="Statut" className="w-full" {...form.register("status")}>
                <option value="">Tous</option>
                {Object.keys(CustomerTaskStatus).map((key) => (
                  <option key={key} value={key}>
                    {customerTaskStatusLabel(key)}
                  </option>
                ))}
              </Select>
              <Select label="Rattachement" className="w-full" {...form.register("scope")}>
                <option value="">Toutes</option>
                <option value="customer">Rattachées à un client</option>
                <option value="internal">Tâches internes</option>
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !results.length ? (
            <EmptyList text="Aucune tâche" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Titre</TableCell>
                  <TableCell header>Client</TableCell>
                  <TableCell header align="center">
                    Priorité
                  </TableCell>
                  <TableCell header align="center">
                    Échéance
                  </TableCell>
                  <TableCell header>Assigné à</TableCell>
                  <TableCell header>Statut</TableCell>
                  {isAdmin && (
                    <TableCell header align="center">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((task) => {
                  const priority = customerTaskPriorityInfo(task.priority);
                  // Une tâche terminée n'est jamais signalée en retard.
                  const due =
                    task.status === CustomerTaskStatus.Done
                      ? "none"
                      : dueDateStatus(task.dueDate);
                  return (
                    <TableRow
                      key={`customer-task-${task.id}`}
                      onClick={() => navigate(`/customer-task/${task.id}/update`)}
                    >
                      <TableCell className="max-w-xs truncate">{task.title}</TableCell>
                      <TableCell>
                        {task.customer?.name ?? (
                          <span className="text-gray-400 italic">Interne</span>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}
                        >
                          {priority.label}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        {task.dueDate ? (
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              due === "none"
                                ? "bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400"
                                : DUE_DATE_STYLES[due]
                            }`}
                            title={due === "none" ? undefined : DUE_DATE_LABELS[due]}
                          >
                            {formatDisplayDate(task.dueDate)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {task.assignedTo
                          ? `${task.assignedTo.firstname} ${task.assignedTo.lastname}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <select
                          className="input"
                          value={task.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleStatusChange(e, task.id)}
                        >
                          {Object.keys(CustomerTaskStatus).map((key) => (
                            <option key={key} value={key}>
                              {customerTaskStatusLabel(key)}
                            </option>
                          ))}
                        </select>
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="center">
                          <span
                            className="text-red-600 text-sm cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(task.id);
                            }}
                          >
                            Supprimer
                          </span>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
